import { useMemo, useState } from "react";
import type { PersonDetailWire } from "@releve/shared";
import { SearchableSelect, type SearchableSelectOption } from "@/components/SearchableSelect";
import { personTypeOverlaps, usePersonIndex } from "@/hooks/usePeople";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useTranslation } from "@/hooks/useTranslation";
import { substringSearchFilter } from "@/lib/substringSearchFilter";
import { PersonCreateDialog } from "./PersonCreateDialog.tsx";
import { QuickCreatePersonDialog } from "./QuickCreatePersonDialog";

const NO_EXCLUDES: string[] = [];

interface PersonSelectProps {
  /** Lands on the combobox trigger — shadcn's `FormControl` injects it so the label associates. */
  id?: string;
  value?: string;
  /** Called with the picked person id, or `""` when the selection is cleared. */
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Only offer people with at least one of these types. */
  personTypes?: string[];
  excludeIds?: string[];
  allowCreate?: boolean;
  /** Types pre-selected in the create form (falls back to `personTypes`). */
  defaultPersonTypes?: string[];
  /**
   * What "Create new" opens: `full` (default) — the fullscreen
   * `PersonCreateDialog`; `quick` — the small name-only
   * `QuickCreatePersonDialog` (a StandardDialog). Use `quick` inside a
   * `FullscreenFormDialog`, which must not host a second fullscreen dialog.
   * `defaultPersonTypes` applies to `full` only.
   */
  createMode?: "full" | "quick";
}

/**
 * Single-select person picker: the org's people index mapped onto the shared
 * `SearchableSelect`. Search matches any field of the index's `search_text`
 * (name, code, email, phone, company, city, …) as a substring.
 */
export function PersonSelect({
  id,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  personTypes,
  excludeIds = NO_EXCLUDES,
  allowCreate = true,
  defaultPersonTypes,
  createMode = "full",
}: PersonSelectProps) {
  const { t } = useTranslation("common");
  const { selectedOrganization } = useOrganization();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // What the user typed in the search, carried into the quick-create dialog.
  const [search, setSearch] = useState("");
  // The person just created here, shown until the index refetch includes them
  // (otherwise the trigger falls back to the placeholder for a round-trip).
  const [created, setCreated] = useState<PersonDetailWire | null>(null);

  // The COMPLETE org index, fetched once and shared with every other picker
  // and the people grid.
  const { data: people = [] } = usePersonIndex(selectedOrganization?.id, { sortByName: true });

  const options = useMemo<SearchableSelectOption[]>(() => {
    const visible = people.filter(
      (person) => !excludeIds.includes(person.id) && personTypeOverlaps(person, personTypes),
    );
    // A selected person hidden by the type filter or excludeIds still needs an
    // option, or the trigger would blank to the placeholder (SearchableSelect
    // resolves the trigger label from `options`). It therefore also shows as a
    // normal, re-selectable row in the list — deliberate, not a filter leak.
    if (value && !visible.some((person) => person.id === value)) {
      const selected = people.find((person) => person.id === value);
      if (selected) visible.unshift(selected);
    }
    const mapped: SearchableSelectOption[] = visible.map((person) => ({
      value: person.id,
      label: person.full_name,
      description: person.email || person.company || person.job_title,
      keywords: [person.search_text],
    }));
    if (created && !people.some((person) => person.id === created.id)) {
      mapped.unshift({ value: created.id, label: created.full_name });
    }
    return mapped;
  }, [people, excludeIds, personTypes, value, created]);

  const handleCreateSuccess = (person: PersonDetailWire) => {
    setCreated(person);
    onValueChange(person.id);
    setShowCreateDialog(false);
  };

  return (
    <>
      <SearchableSelect
        id={id}
        options={options}
        value={value}
        onValueChange={onValueChange}
        filter={substringSearchFilter}
        onSearchChange={setSearch}
        placeholder={placeholder ?? t("people.select.placeholder")}
        searchPlaceholder={t("people.select.searchPlaceholder")}
        emptyText={t("people.select.noResults")}
        onCreateNew={allowCreate ? () => setShowCreateDialog(true) : undefined}
        createNewText={t("people.select.createNew")}
        disabled={disabled}
        className={className}
      />

      {showCreateDialog && createMode === "quick" && (
        <QuickCreatePersonDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onCreated={handleCreateSuccess}
          initialName={search}
        />
      )}

      {showCreateDialog && createMode === "full" && (
        <PersonCreateDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onSuccess={handleCreateSuccess}
          defaultPersonTypes={personTypes || defaultPersonTypes}
          navigateOnCreate={false}
        />
      )}
    </>
  );
}
